<template>
  <Transition name="modal">
    <div v-if="show" class="modal-overlay" @click="$emit('close')">
      <Transition name="slide">
        <div class="modal-content" v-if="show" @click.stop>
          <!-- Header Actions -->
          <div class="modal-header">
            <div class="header-actions">
              <!-- <button
                class="action-btn download-btn"
                @click="downloadResume"
                v-tooltip.left="'Download Resume'"
              >
                <i class="fas fa-download"></i>
              </button> -->
              <button
                class="action-btn close-btn"
                @click="$emit('close')"
                v-tooltip.left="'Close'"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          
          <div class="resume-container" v-if="resumeData && resumeData._source">
            <!-- Hero Section -->
            <div class="hero-section">
              <div class="hero-content">
                <h1 class="candidate-name">{{ getPersonalInfo('full_name', 'Unknown Candidate') }}</h1>
                
                <!-- Current Position -->
                <div class="current-position" v-if="currentPosition">
                  <span class="position-title">{{ currentPosition.title }}</span>
                  <span class="at-separator">at</span>
                  <span class="company-name">{{ currentPosition.company }}</span>
                </div>
                
                <!-- Contact Bar -->
                <div class="contact-bar">
                  <a
                    v-if="hasEmail"
                    :href="`mailto:${Array.isArray(getPersonalInfo('email')) ? getPersonalInfo('email')[0] : getPersonalInfo('email')}`"
                    class="contact-item"
                  >
                                <i class="fas fa-envelope"></i>
                  <span>{{ Array.isArray(getPersonalInfo('email')) ? getPersonalInfo('email')[0] : getPersonalInfo('email') }}</span>
                </a>
                  
                <a
                v-if="hasPhone"
                :href="`tel:${Array.isArray(getPersonalInfo('phone')) ? getPersonalInfo('phone')[0] : getPersonalInfo('phone')}`"
                class="contact-item"
              >
                <i class="fas fa-phone"></i>
                <span>{{ Array.isArray(getPersonalInfo('phone')) ? getPersonalInfo('phone')[0] : getPersonalInfo('phone') }}</span>
              </a>
            
                  <a v-if="hasLinkedin" :href="getLinkedInUrl()" target="_blank" class="contact-item linkedin">
                    <i class="fab fa-linkedin"></i>
                    <span>LinkedIn Profile</span>
                  </a>
                  
                  <div v-if="hasLocation" class="contact-item">
                    <i class="fas fa-map-marker-alt"></i>
                    <span>{{ formatLocation() }}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Summary Section -->
            <div class="content-section" v-if="resumeData._source.professional_summary || resumeData._source.candidate_summary">
              <div class="section-header">
                <i class="fas fa-user-circle"></i>
                <h2>{{ resumeData._source.professional_summary ? 'Professional Summary' : 'AI-Generated Summary' }}</h2>
              </div>
              <div class="section-content">
                <p class="summary-text">{{ resumeData._source.professional_summary || resumeData._source.candidate_summary }}</p>
              </div>
            </div>
            
            <!-- Skills Section -->
            <div class="content-section" v-if="hasSkills">
              <div class="section-header">
                <i class="fas fa-tools"></i>
                <h2>Skills & Expertise</h2>
              </div>
              <div class="section-content">
                <div class="skills-container">
                  <!-- Technical Skills -->
                  <div v-if="hasTechnicalSkills" class="skill-category">
                    <h3 class="category-title">
                      <i class="fas fa-code"></i>
                      Technical Skills
                    </h3>
                    <div class="skills-grid">
                      <span class="skill-chip technical" v-for="(skill, index) in resumeData._source.skills.technical" :key="`tech-${index}`">
                        {{ skill }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Professional Skills -->
                  <div v-if="hasInferredSkills" class="skill-category">
                    <h3 class="category-title">
                      <i class="fas fa-briefcase"></i>
                      Professional Skills
                    </h3>
                    <div class="skills-grid">
                      <span class="skill-chip professional" v-for="(skill, index) in resumeData._source.skills.inferred_skills" :key="`prof-${index}`">
                        {{ skill }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Languages -->
                  <div v-if="hasLanguages" class="skill-category">
                    <h3 class="category-title">
                      <i class="fas fa-language"></i>
                      Languages
                    </h3>
                    <div class="skills-grid">
                      <span class="skill-chip language" v-for="(lang, index) in resumeData._source.skills.languages" :key="`lang-${index}`">
                        {{ lang }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Work Experience -->
            <div class="content-section" v-if="hasWorkExperience">
              <div class="section-header">
                <i class="fas fa-briefcase"></i>
                <h2>Work Experience</h2>
              </div>
              <div class="section-content">
                <div class="timeline">
                  <div class="timeline-item" v-for="(work, index) in resumeData._source.work_experience" :key="`work-${index}`">
                    <div class="timeline-marker"></div>
                    <div class="timeline-content">
                      <div class="job-header">
                        <h3 class="job-title">{{ work.job_title || 'Position' }}</h3>
                        <span class="job-period">{{ formatDateRange(work.employment_dates) }}</span>
                      </div>
                      <div class="job-company">
                        <i class="fas fa-building"></i>
                        {{ work.company_name || 'Company' }}
                        <span class="job-duration" v-if="work.employment_dates?.employment_period">
                          • {{ formatExperiencePeriod(work.employment_dates.employment_period) }}
                        </span>
                      </div>
                      <div class="job-meta" v-if="work.seniority || work.contract_type || work.work_mode">
                        <span class="meta-badge" v-if="work.seniority">
                          <i class="fas fa-layer-group"></i>
                          {{ work.seniority }}
                        </span>
                        <span class="meta-badge" v-if="work.contract_type">
                          <i class="fas fa-file-contract"></i>
                          {{ work.contract_type }}
                        </span>
                        <span class="meta-badge" v-if="work.work_mode">
                          <i class="fas fa-laptop-house"></i>
                          {{ work.work_mode }}
                        </span>
                      </div>
                      <ul class="responsibilities" v-if="work.responsibilities_and_achievements?.length">
                        <li v-for="(resp, rIndex) in work.responsibilities_and_achievements" :key="`resp-${rIndex}`">
                          {{ resp }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Education -->
            <div class="content-section" v-if="hasEducation">
              <div class="section-header">
                <i class="fas fa-graduation-cap"></i>
                <h2>Education</h2>
              </div>
              <div class="section-content">
                <div class="education-grid">
                  <div class="education-card" v-for="(edu, index) in resumeData._source.education" :key="`edu-${index}`">
                    <div class="degree-icon">
                      <i class="fas fa-university"></i>
                    </div>
                    <div class="education-details">
                      <h3 class="degree-title">{{ edu.degree || 'Degree' }}</h3>
                      <div class="institution-name">
                        <i class="fas fa-school"></i>
                        {{ edu.institution || 'Institution' }}
                      </div>
                      <div class="education-meta">
                        <span v-if="edu.major" class="major">
                          <i class="fas fa-book"></i>
                          {{ edu.major }}
                        </span>
                        <span v-if="edu.graduation_date" class="graduation-date">
                          <i class="fas fa-calendar-alt"></i>
                          {{ formatGraduationDate(edu.graduation_date) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<script>
export default {
  name: "ResumeDetailView",
  props: {
    show: Boolean,
    resumeData: Object,
  },
  computed: {
    currentPosition() {
      if (this.hasWorkExperience && this.resumeData._source.work_experience[0]) {
        const recent = this.resumeData._source.work_experience[0];
        return {
          title: recent.job_title,
          company: recent.company_name
        };
      }
      return null;
    },
    
    hasPersonalInfo() {
      return this.resumeData?._source?.personal_information != null;
    },
    
    hasEmail() {
      const emails = this.getPersonalInfo('email');
      return emails && emails.length > 0;
    },
    
    hasPhone() {
      const phones = this.getPersonalInfo('phone');
      return phones && phones.length > 0;
    },
    
    hasLinkedin() {
      const linkedin = this.getPersonalInfo('linkedin');
      return linkedin && linkedin.length > 0;
    },
    
    hasLocation() {
      const pi = this.resumeData?._source?.personal_information;
      return pi && (pi.location || (pi.locations && pi.locations.length > 0));
    },
    
    hasWorkExperience() {
      return this.resumeData?._source?.work_experience?.length > 0;
    },
    
    hasEducation() {
      return this.resumeData?._source?.education?.length > 0;
    },
    
    hasSkills() {
      const skills = this.resumeData?._source?.skills;
      return skills && (
        (skills.technical?.length > 0) ||
        (skills.inferred_skills?.length > 0) ||
        (skills.languages?.length > 0)
      );
    },
    
    hasTechnicalSkills() {
      return this.resumeData?._source?.skills?.technical?.length > 0;
    },
    
    hasInferredSkills() {
      return this.resumeData?._source?.skills?.inferred_skills?.length > 0;
    },
    
    hasLanguages() {
      return this.resumeData?._source?.skills?.languages?.length > 0;
    }
  },
  methods: {
    getPersonalInfo(field, defaultValue = null) {
      const pi = this.resumeData?._source?.personal_information;
      if (!pi) return defaultValue;
      
      const value = pi[field];
      if (Array.isArray(value)) {
        return value.length > 0 ? value : defaultValue;
      }
      return value || defaultValue;
    },
    
    getLinkedInUrl() {
      const linkedin = this.getPersonalInfo('linkedin');
      if (!linkedin || !linkedin[0]) return null;
      
      let url = linkedin[0];
      if (url.includes('linkedin.com')) {
        return url.startsWith('http') ? url : `https://${url}`;
      }
      
      const cleanSuffix = url.replace(/^\/+/, '');
      return `https://www.linkedin.com/in/${cleanSuffix}`;
    },
    
    formatLocation() {
      const pi = this.resumeData?._source?.personal_information;
      if (!pi) return '';

    // If location is an object with city/country
    if (pi.location && (pi.location.city || pi.location.country)) {
      const { city, country } = pi.location;
      return [city, country].filter(Boolean).join(', ');
    }

    // If fallback is locations array
    if (pi.locations && pi.locations.length > 0) {
      return pi.locations.join(' • ');
    }

return '';
},
    
    formatDateRange(employment_dates) {
      if (!employment_dates) return '';
      
      const start = employment_dates.start || '';
      const end = employment_dates.end || 'Present';
      
      return `${start} - ${end}`;
    },
    
    formatExperiencePeriod(months) {
      if (!months) return '';
      
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      
      if (years > 0 && remainingMonths > 0) {
        return `${years} ${years === 1 ? 'year' : 'years'}, ${remainingMonths} ${remainingMonths === 1 ? 'month' : 'months'}`;
      } else if (years > 0) {
        return `${years} ${years === 1 ? 'year' : 'years'}`;
      } else {
        return `${months} ${months === 1 ? 'month' : 'months'}`;
      }
    },
    
    formatGraduationDate(date) {
      if (!date) return '';
      
      // Handle various date formats
      if (typeof date === 'string') {
        if (date.includes('-')) {
          const parts = date.split('-');
          return parts[0]; // Just return the year
        }
        return date;
      }
      
      return date.toString();
    },
    
    downloadResume() {
      // Implement download functionality
      const candidateId = this.resumeData?._source?.metadata?.candidate_id;
      if (candidateId) {
        // You can reuse the download logic from GallerySection
        console.log('Download resume for:', candidateId);
      }
    }
  }
};
</script>

<style scoped>
/* CSS Variables matching the aesthetic */
:root {

  --primary-color: #ea5e13;
  --background-color: #262624;
  --surface-color: rgba(255, 255, 255, 0.05);
  --surface-hover: rgba(255, 255, 255, 0.08);
  --border-color: rgba(250, 249, 246, 0.1);
  --text-primary: #ffffff;
  --text-secondary: rgba(250, 249, 246, 0.7);
  --text-muted: rgba(250, 249, 246, 0.5);
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem; /* Reduced from 2rem */
}

/* Modal Content */
.modal-content {
  zoom:0.8;
  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 1rem;
  width: 100%;
  max-width: 750px; /* Reduced from 900px */
  max-height:90%; /* Changed from max-height: 90vh */
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

/* Modal Header */
.modal-header {
  position: absolute;
  top: 0;
  right: 0;
  padding: 1.5rem;
  z-index: 10;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.action-btn {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: var(--surface-hover);
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.download-btn i {
  font-size: 1rem;
}

.close-btn i {
  font-size: 1.25rem;
}

/* Resume Container */
.resume-container {
  overflow-y: auto;
  padding: 2rem;
  flex: 1; /* Take remaining space */
  /* Remove max-height: 90vh */
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, var(--surface-color), rgba(234, 94, 19, 0.05));
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  padding: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
}

.candidate-name {
  font-size: 2.5rem;
  font-weight: 900;
  color: var(--text-primary);
  margin: 0 0 0.75rem;
  line-height: 1.1;
}

.current-position {
  font-size: 1.25rem;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
}

.position-title {
  font-weight: 600;
  color: var(--text-primary);
}

.at-separator {
  font-style: italic;
  opacity: 0.7;
  margin: 0 0.5rem;
}

.company-name {
  color: var(--primary-color);
  font-weight: 500;
}

/* Contact Bar */
.contact-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: center;
  margin-top: 1.5rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.contact-item:hover {
  color: var(--primary-color);
  transform: translateY(-1px);
}

.contact-item i {
  font-size: 1rem;
  color: var(--primary-color);
}

.contact-item.linkedin {
  color: #0077b5;
}

.contact-item.linkedin:hover {
  color: #005582;
}

/* Content Sections */
.content-section {
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  margin-bottom: 1.5rem;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  background: rgba(234, 94, 19, 0.05);
  border-bottom: 1px solid var(--border-color);
}

.section-header i {
  color: var(--primary-color);
  font-size: 1.25rem;
}

.section-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
}

.section-content {
  padding: 1.5rem;
}

/* Summary */
.summary-text {
  line-height: 1.8;
  color: var(--text-secondary);
  margin: 0;
  font-size: 0.95rem;
}

/* Skills */
.skills-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.skill-category {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.category-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.category-title i {
  color: var(--primary-color);
  font-size: 0.875rem;
}

.skills-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/*   .skill-chip with: */
.skill-chip {
  padding: 0.25rem 0.75rem;
  background: var(--primary-color);
  border: 1px solid var(--primary-color);
  border-radius: 1rem;
  font-size: 0.813rem;
  color: white;
  font-weight: 500;
}

.skill-chip.technical {
  background: rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.3);
  color: #347cf9;
}

.skill-chip.professional {
  background: rgba(34, 197, 94, 0.15);
  border-color: rgba(34, 197, 94, 0.3);
  color: #166132;
}

.skill-chip.language {
  background: rgba(234, 94, 19, 0.15);
  border-color: rgba(234, 94, 19, 0.3);
  color: #fdba74;
}
:root[data-theme="light"] .skill-chip.technical {
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.25);
  color: #1d4ed8;
}

:root[data-theme="light"] .skill-chip.professional {
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.25);
  color: #166534;
}

:root[data-theme="light"] .skill-chip.language {
  background: rgba(234, 94, 19, 0.1);
  border-color: rgba(234, 94, 19, 0.25);
  color: #9a3412;
}
:root[data-theme="dark"] .skill-chip.technical {
  background: rgba(59, 130, 246, 0.25);
  border-color: rgba(59, 130, 246, 0.45);
  color: #93c5fd;
}

:root[data-theme="dark"] .skill-chip.professional {
  background: rgba(34, 197, 94, 0.25);
  border-color: rgba(34, 197, 94, 0.45);
  color: #86efac;
}

:root[data-theme="dark"] .skill-chip.language {
  background: rgba(234, 94, 19, 0.25);
  border-color: rgba(234, 94, 19, 0.45);
  color: #fed7aa;
}

.skill-chip:hover {
  transform: translateY(-1px);
}
/* Timeline for Work Experience */
.timeline {
  position: relative;
  padding-left: 2rem;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 0.5rem;
  top: 0.5rem;
  bottom: 0.5rem;
  width: 2px;
  background: var(--border-color);
}

.timeline-item {
  position: relative;
  margin-bottom: 2rem;
}

.timeline-item:last-child {
  margin-bottom: 0;
}

.timeline-marker {
  position: absolute;
  left: -1.5rem;
  top: 0.25rem;
  width: 1rem;
  height: 1rem;
  background: var(--primary-color);
  border: 3px solid var(--background-color);
  border-radius: 50%;
}

.timeline-content {
  background: var(--surface-hover);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  padding: 1.25rem;
}

.job-header {
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.job-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary);
}

.job-period {
  font-size: 0.875rem;
  color: var(--primary-color);
  font-weight: 500;
}

.job-company {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
}

.job-company i {
  color: var(--primary-color);
  font-size: 0.875rem;
}

.job-duration {
  color: var(--text-muted);
  font-size: 0.875rem;
}

.job-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.meta-badge {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  background: rgba(234, 94, 19, 0.1);
  border: 1px solid rgba(234, 94, 19, 0.2);
  border-radius: 1rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.meta-badge i {
  color: var(--primary-color);
  font-size: 0.75rem;
}

.responsibilities {
  margin: 0;
  padding-left: 1.25rem;
  list-style: none;
}

.responsibilities li {
  position: relative;
  margin-bottom: 0.5rem;
  line-height: 1.6;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.responsibilities li::before {
  content: '▸';
  position: absolute;
  left: -1.25rem;
  color: var(--primary-color);
  font-weight: bold;
}

/* Education */
.education-grid {
  display: grid;
  gap: 1rem;
}

.education-card {
  display: flex;
  gap: 1.25rem;
  background: var(--surface-hover);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  padding: 1.25rem;
}

.degree-icon {
  flex-shrink: 0;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(234, 94, 19, 0.1);
  border-radius: 0.5rem;
  color: var(--primary-color);
}

.degree-icon i {
  font-size: 1.5rem;
}

.education-details {
  flex: 1;
}

.degree-title {
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary);
}

.institution-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
}

.institution-name i {
  color: var(--primary-color);
  font-size: 0.875rem;
}

.education-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.major, .graduation-date {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: var(--text-muted);
}

.major i, .graduation-date i {
  color: var(--primary-color);
  font-size: 0.75rem;
}

/* Animations */
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  transform: translateY(20px);
  opacity: 0;
}

.slide-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

/* Scrollbar styling */
.resume-container::-webkit-scrollbar {
  width: 8px;
}

.resume-container::-webkit-scrollbar-track {
  background: var(--surface-color);
  border-radius: 4px;
}

.resume-container::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 4px;
}

.resume-container::-webkit-scrollbar-thumb:hover {
  background: var(--primary-color);
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    max-width: 100%;
    margin: 1rem;
  }
  
  .hero-section {
    padding: 1.5rem;
  }
  
  .candidate-name {
    font-size: 2rem;
  }
  
  .contact-bar {
    gap: 1rem;
    flex-direction: column;
    align-items: center;
  }
  
  .job-header {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .timeline {
    padding-left: 1.5rem;
  }
}
</style>